import { useCallback, useMemo, useRef, useState } from 'react';

import { UseQueryResult } from '@tanstack/react-query';
import { ComboboxData, ComboboxItem, ComboboxItemGroup } from 'ui';
import { useDebouncedValue } from 'ui/Hooks';

import {
  SearchAbleSelectData,
  SearchableMultiSelectWithOptionData,
  SearchableSelectWithOptionData,
  UseCreateSearchFn,
} from './types';
import { isGroup } from './utils/isGroup';

type SelectTypes = 'select' | 'multi-select';
type FetchSource<T extends SelectTypes> = T extends 'select'
  ? SearchableSelectWithOptionData['useFetchSource']
  : SearchableMultiSelectWithOptionData['useFetchSource'];

type MultiSelectDataType = ComboboxData;

/** Util to get the value of an item regardless of type */
const getItemToAdd = (item: ComboboxItem | string) => {
  if (typeof item === 'string') return item;
  return item.value;
};

/**
 * Takes valid ComboboxData and adds it to the data list ensuring grouping is maintained and combined with the existing data
 * handles deduping between grouped items and loose leaf items
 *
 * @param oldData - The existing data in the data ref
 * @param newData - The new data to add to the data ref
 * @returns The updated data list
 */
const addDataToDataList = (oldData: MultiSelectDataType, newData: MultiSelectDataType) => {
  /** Groups is a map of all unique groups in the total data set- this is used to ensure we never add the same group twice */
  const groups = new Map<string, ComboboxItemGroup>();
  /** Items or is a set of all unique loose leaf items in the total data set- this is used to ensure we never add the loose leaf items twice */
  const items = new Map<string, ComboboxItem | string>();

  // Add existing data to the set of groups and loose leaf items
  oldData.forEach((item) => {
    if (isGroup(item)) {
      groups.set(item.group, item);
    } else {
      items.set(getItemToAdd(item), item);
    }
  });

  /** Add new data to the data ref ensuring grouping is maintained and combined with the existing data to dedupe */
  newData.forEach((item) => {
    // If the item is a group, check if the group already exists in the set of groups
    if (isGroup(item)) {
      const currentGroup = groups.get(item.group);
      if (currentGroup) {
        // Add all items in the current group to the set of items new items- this will be used to dedupe items between groups and items
        const newItems = new Map<string, ComboboxItem | string>(
          currentGroup.items.map((item) => [getItemToAdd(item), item])
        );

        // Add all items in the group to the set of items
        item.items.forEach((item) => {
          const itemToAdd = getItemToAdd(item);
          newItems.set(itemToAdd, item);
        });

        // Add all deduped items to the group
        groups.set(item.group, {
          ...currentGroup,
          // Add all deduped items to the group
          items: Array.from(newItems.values()),
        });
      } else {
        // If the group doesn't exist, add it to the set of groups
        groups.set(item.group, item);
      }
    } else {
      // If the item is not a group, add it to the set of items
      items.set(getItemToAdd(item), item);
    }
  });

  // Goes through each groups items and check if the item is also included in loose leaf items- if it is, then we remove the loose leaf item preferring to keep the group item
  groups.forEach((group) => {
    group.items.forEach((item) => {
      if (items.has(getItemToAdd(item))) {
        items.delete(getItemToAdd(item));
      }
    });
  });

  return [...groups.values(), ...Array.from(items.values())];
};

const useSearchableInput = <Ttype extends SelectTypes, TUseFetchSource extends FetchSource<Ttype>>({
  type,
  useFetchSource,
  // Not used, we could use this if we need to provide a way for us to set the initial value.
  onChange,
  initialData,
}: {
  useFetchSource: TUseFetchSource;
  type: Ttype;
  onChange?: (value: string[]) => void;
  initialData?: MultiSelectDataType;
}) => {
  /** Stores unique values from the initial data and data from useFetchSource */
  const dataRef = useRef(initialData ?? []);

  const [search, setSearch] = useState<string | undefined>();

  /**
   * Stores the selected values with their original type intact
   *  i.e if values is [1,2,3] then _multiSelectValue will be [{value: 1, label: 'one' }, {value: 2, label: 'two'}, {value: 3, label: 'three'}]
   *  this is used to ensure that the selected values can be displayed correctly outside of the multi select
   */
  const [_multiSelectValue, SetMultiSelectValue] = useState<(string | ComboboxItem)[]>([]);

  // Handle debouncing the search value so we don't overload the useHook on each keystroke
  const [debouncedSearchValue] = useDebouncedValue(search, 500, { leading: true });
  const { data, fetchStatus } = useFetchSource(debouncedSearchValue, type) ?? {};
  // Show loading state if the search is still debounced or we're actively fetching
  const isFetching = useMemo(
    () => fetchStatus === 'fetching' || search !== debouncedSearchValue,
    [fetchStatus, search, debouncedSearchValue]
  );

  const props = useMemo(
    () => ({
      // TODO: This should be updated to use the nothingFoundMessage prop
      // nothingFound: isFetching ? undefined : 'Nothing found',
      searchable: true,
      clearable: true,
    }),
    []
  );

  /**
   * De-duped data from the fetched data and the multi select value if present.
   */
  const _fetchedData = useMemo(() => {
    dataRef.current = addDataToDataList(dataRef.current, data ?? []);

    return dataRef.current;
  }, [data]);

  const onMultiSelectChange = useCallback(
    (values: string[]) => {
      // Clear the current selection first
      SetMultiSelectValue([]);

      // If no values are selected, we're done
      if (values.length === 0) {
        onChange?.(values);
        return;
      }

      // Find the selected items in the fetched data
      const selectedItems: (string | ComboboxItem)[] = [];

      values.forEach((value) => {
        // Search through all items in fetched data
        for (const item of _fetchedData) {
          if (typeof item === 'string') {
            if (item === value) {
              selectedItems.push(item);
              break;
            }
          } else if (isGroup(item)) {
            // Search within group items
            const foundInGroup = item.items.find((groupItem) => {
              if (typeof groupItem === 'string') {
                return groupItem === value;
              }
              return groupItem.value === value;
            });
            if (foundInGroup) {
              selectedItems.push(foundInGroup);
              break;
            }
          } else {
            // Regular ComboboxItem
            if (item.value === value) {
              selectedItems.push(item);
              break;
            }
          }
        }
      });

      // Set the multi select value to the found items
      SetMultiSelectValue(selectedItems);
      onChange?.(values);
    },
    [_fetchedData, onChange]
  );

  /**
   * Takes a search value and adds it to the data ref if saveToDataRef is true
   * Returns the new option
   */
  const addCreatedOption = useCallback((searchValue: string, saveToDataRef: boolean = false) => {
    const newOption = { value: searchValue, label: searchValue, type: 'created-option' };
    const newData = addDataToDataList(dataRef.current, [newOption]);
    if (saveToDataRef) {
      dataRef.current = newData;
    }
    return newOption;
  }, []);

  return {
    isLoading: isFetching,
    fetchedData: _fetchedData,
    props,
    onSearchChange: setSearch,
    searchValue: search,
    onMultiSelectChange,
    _multiSelectValue,
    addCreatedOption,
  };
};

const useCreateSearchFn = <
  TQuery extends Pick<
    UseQueryResult<unknown, unknown>,
    'data' | 'isLoading' | 'isError' | 'isPending' | 'isSuccess' | 'fetchStatus'
  >,
  TParams extends Record<string, unknown>,
  TSearchData extends SearchAbleSelectData,
>({
  useSearchFn,
  select,
  params,
  searchFields,
}: UseCreateSearchFn<TQuery, TParams, TSearchData>) => {
  const useFinishedFunc = <TSearch extends string>(debouncedValue?: TSearch) => {
    const debouncedSearchValueObject = useMemo(() => {
      if (!debouncedValue) return {}; // If there's no value, don't actually create a search
      const tempSearch = {};
      searchFields?.forEach((field) =>
        Object.assign(tempSearch, { [field]: { value: debouncedValue } })
      );
      return tempSearch;
    }, [debouncedValue]);

    const { data, isLoading, isError, fetchStatus } = useSearchFn(debouncedSearchValueObject, {
      ...params,
    });
    return {
      data: select(data),
      isLoading,
      isError,
      fetchStatus,
    };
  };
  // Only return a new function when the provided params have changed. IF we need to be using dynamic search fields, this could be updated.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(useFinishedFunc, [params]);
};

export { useCreateSearchFn, useSearchableInput };
